import { useRouter } from 'next/router';
import { type FC, useEffect, useRef } from 'react';
import { CONTACT_FORMS } from 'src/lib/constants';
import { useTrackContactRoutingOutcome } from './ContactRoutingExposure';
import { contactRoutingConfig } from './config';
import { useContactRoutingEligibility } from './use-contact-routing-eligibility';
import {
  getAssignedContactRoutingArm,
  getContactRoutingOutcome,
  useContactRoutingFlagValue,
} from './use-contact-routing-outcome';

const { flag } = contactRoutingConfig;

const CONTACT_FORM_SELECTOR = Array.from(CONTACT_FORMS, (key) => {
  return `form[data-marketo-key="${key}"]`;
}).join(',');

const decodeHashId = (hash: string) => {
  try {
    return decodeURIComponent(hash.slice(1));
  } catch {
    return hash.slice(1);
  }
};

const isContactFormSection = (hash: string) => {
  if (hash.length < 2) {
    return false;
  }
  const id = decodeHashId(hash);
  return Array.from(document.querySelectorAll(CONTACT_FORM_SELECTOR)).some(
    (form) => {
      return form.parentElement?.closest('[id]')?.id === id;
    },
  );
};

const hasOptedOut = () => {
  return (
    new URLSearchParams(window.location.search).get(flag.optOutParam) === 'off'
  );
};

const isPlainLeftClick = (event: MouseEvent) => {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
};

const getSamePageHash = (event: MouseEvent) => {
  const link =
    event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (
    !(link instanceof HTMLAnchorElement) ||
    link.target === '_blank' ||
    link.origin !== window.location.origin ||
    link.pathname !== window.location.pathname
  ) {
    return '';
  }
  return link.hash;
};

const ArmJumpLinks: FC<{ arm: string }> = ({ arm }) => {
  const eligibility = useContactRoutingEligibility();
  const trackOutcome = useTrackContactRoutingOutcome();
  const basePath = useRouter()?.basePath ?? '';
  const hasReported = useRef(false);
  const hasCheckedLandingHash = useRef(false);

  useEffect(() => {
    const outcome = getContactRoutingOutcome(arm, eligibility, arm);
    if (outcome.type === 'none' || hasOptedOut()) {
      return undefined;
    }
    const getContactPageUrl = () => {
      const url = new URL(
        `${basePath}${flag.controlPagePath}`,
        window.location.origin,
      );
      url.search = window.location.search;
      return url.toString();
    };
    const shouldRoute = outcome.type === 'exposed' && arm === flag.variant;

    const enterExperiment = () => {
      if (shouldRoute) {
        return true;
      }
      if (!hasReported.current) {
        hasReported.current = true;
        trackOutcome(outcome);
      }
      return false;
    };

    if (!hasCheckedLandingHash.current) {
      hasCheckedLandingHash.current = true;
      if (isContactFormSection(window.location.hash) && enterExperiment()) {
        window.location.replace(getContactPageUrl());
        return undefined;
      }
    }

    const onClick = (event: MouseEvent) => {
      if (
        !isPlainLeftClick(event) ||
        !isContactFormSection(getSamePageHash(event)) ||
        !enterExperiment()
      ) {
        return;
      }
      event.preventDefault();
      window.location.assign(getContactPageUrl());
    };

    window.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('click', onClick, true);
    };
  }, [arm, eligibility, trackOutcome, basePath]);

  return null;
};

export const ContactRoutingJumpLinks: FC = () => {
  const arm = getAssignedContactRoutingArm(useContactRoutingFlagValue());
  return arm ? <ArmJumpLinks arm={arm} /> : null;
};
